import { useState } from "react";
import { Sparkle, PencilSimple, Trash, Check, X, ArrowRight, CircleNotch } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const TitleRow = ({ t, onUpdate, onDelete, onUse }) => {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(t.text);

  const commit = () => {
    const v = value.trim();
    if (v && v !== t.text) onUpdate(t.id, v);
    setEditing(false);
  };

  return (
    <li
      data-testid={`title-item-${t.id}`}
      className="group border-b border-border/40 px-4 py-3 hover:bg-[#111] transition-colors animate-fade-in-up"
    >
      {editing ? (
        <div className="flex items-center gap-2">
          <Input
            data-testid={`title-edit-input-${t.id}`}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            autoFocus
            onKeyDown={(e) => {
              if (e.key === "Enter") commit();
              if (e.key === "Escape") {
                setValue(t.text);
                setEditing(false);
              }
            }}
            className="bg-background border-border rounded-none h-9 text-sm focus-visible:ring-primary focus-visible:ring-1 focus-visible:ring-offset-0"
          />
          <button
            data-testid={`title-save-${t.id}`}
            onClick={commit}
            className="h-9 w-9 flex items-center justify-center border border-border hover:border-primary hover:text-primary transition-colors"
          >
            <Check size={14} />
          </button>
          <button
            data-testid={`title-cancel-${t.id}`}
            onClick={() => {
              setValue(t.text);
              setEditing(false);
            }}
            className="h-9 w-9 flex items-center justify-center border border-border hover:border-muted-foreground text-muted-foreground transition-colors"
          >
            <X size={14} />
          </button>
        </div>
      ) : (
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <p
              className={`text-[13.5px] leading-snug ${
                t.is_used ? "text-muted-foreground line-through" : "text-white"
              }`}
            >
              {t.text}
            </p>
            {t.text_tr && (
              <p
                className={`text-[12px] leading-snug mt-1 ${
                  t.is_used ? "text-muted-foreground/50 line-through" : "text-muted-foreground"
                }`}
              >
                TR: {t.text_tr}
              </p>
            )}
            {t.is_used && (
              <span className="label-mono text-primary mt-1 inline-block">// used</span>
            )}
          </div>
          <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
            <button
              data-testid={`title-use-${t.id}`}
              onClick={() => onUse(t)}
              className="h-7 w-7 flex items-center justify-center border border-border hover:border-primary hover:bg-primary hover:text-white transition-colors"
              title="Makale oluştur"
            >
              <ArrowRight size={12} weight="bold" />
            </button>
            <button
              data-testid={`title-edit-${t.id}`}
              onClick={() => setEditing(true)}
              className="h-7 w-7 flex items-center justify-center border border-border hover:border-white text-muted-foreground hover:text-white transition-colors"
              title="Düzenle"
            >
              <PencilSimple size={12} />
            </button>
            <button
              data-testid={`title-delete-${t.id}`}
              onClick={() => {
                if (window.confirm("Bu başlık silinsin mi?")) onDelete(t.id);
              }}
              className="h-7 w-7 flex items-center justify-center border border-border hover:border-primary hover:text-primary text-muted-foreground transition-colors"
              title="Sil"
            >
              <Trash size={12} />
            </button>
          </div>
        </div>
      )}
    </li>
  );
};

export const TitlePanel = ({ channel, titles, loading, onGenerate, onUpdate, onDelete, onUseTitle }) => {
  return (
    <aside className="h-full flex flex-col bg-background" data-testid="title-panel">
      <div className="p-4 border-b border-border/60">
        <div className="flex items-center justify-between mb-3">
          <span className="label-mono text-muted-foreground">AI Titles</span>
          <span className="label-mono text-muted-foreground">
            {String(titles.length).padStart(2, "0")} idea{titles.length === 1 ? "" : "s"}
          </span>
        </div>
        <Button
          data-testid="generate-titles-btn"
          onClick={onGenerate}
          disabled={!channel || loading}
          className="w-full h-11 rounded-none bg-primary hover:bg-[#D63028] disabled:bg-[#1A1A1A] disabled:text-muted-foreground text-white font-bold uppercase tracking-wider text-xs transition-colors"
        >
          {loading ? (
            <>
              <CircleNotch size={16} className="mr-2 animate-spin" />
              Üretiliyor…
            </>
          ) : (
            <>
              <Sparkle size={16} weight="fill" className="mr-2" />
              15 Başlık Üret
            </>
          )}
        </Button>
        {channel && (
          <p className="label-mono text-muted-foreground mt-3 truncate">
            → {channel.name} · {channel.language.toUpperCase()}
          </p>
        )}
      </div>

      <div className="flex-1 overflow-y-auto" data-testid="title-list">
        {!channel && (
          <EmptyHint text="Bir kanal seç" />
        )}
        {channel && titles.length === 0 && !loading && (
          <EmptyHint text="Henüz başlık yok. AI ile üret." />
        )}
        {loading && titles.length === 0 && <SkeletonList />}
        <ul>
          {titles.map((t) => (
            <TitleRow
              key={t.id}
              t={t}
              onUpdate={onUpdate}
              onDelete={onDelete}
              onUse={onUseTitle}
            />
          ))}
        </ul>
      </div>
    </aside>
  );
};

const EmptyHint = ({ text }) => (
  <div className="p-8 text-center">
    <div className="font-mono text-[10px] text-muted-foreground leading-tight whitespace-pre">
{`┌─────────────┐
│             │
│   /  \\  /   │
│  /    \\/    │
│             │
└─────────────┘`}
    </div>
    <p className="label-mono text-muted-foreground mt-3">{text}</p>
  </div>
);

const SkeletonList = () => (
  <ul>
    {Array.from({ length: 8 }).map((_, i) => (
      <li key={i} className="border-b border-border/40 px-4 py-4">
        <div className="h-3 bg-[#151515] w-[85%] mb-2 animate-pulse-line" />
        <div className="h-3 bg-[#151515] w-[55%] animate-pulse-line" style={{ animationDelay: `${i * 80}ms` }} />
      </li>
    ))}
  </ul>
);

export default TitlePanel;
